import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { fetchMetaOverallReport } from '../../utils/api/reportApi';
import { Section, Row } from './ReportUIPrimitives';
import styles from './MetaReportViewer.module.css';
import OverallTab from './helpers/metaReportViewer/OverallTab';

const attachAssessmentToMatrixRows = (section, adsetKey, assessment) => {
  if (!section?.rows) return section;
  return {
    ...section,
    rows: section.rows.map((row) =>
      row?.adset_id === adsetKey || row?.adset_name === adsetKey
        ? { ...row, adset_assessment: assessment }
        : row,
    ),
  };
};

const attachAdAssessmentToRows = (section, adKey, assessment) => {
  if (!section?.rows) return section;
  return {
    ...section,
    rows: section.rows.map((row) =>
      row?.ad_id === adKey || row?.ad_name === adKey
        ? { ...row, ads_level_assessment: assessment }
        : row,
    ),
  };
};

const attachCampaignAssessmentToRows = (section, campaignKey, assessment) => {
  if (!section?.rows) return section;
  return {
    ...section,
    rows: section.rows.map((row) =>
      row?.campaign_id === campaignKey || row?.campaign_name === campaignKey
        ? { ...row, campaign_assessment: assessment }
        : row,
    ),
  };
};

const patchRangeHelper = (rangeHelper, patchSection) => {
  if (!rangeHelper) return rangeHelper;
  return {
    ...rangeHelper,
    by_month: rangeHelper.by_month
      ? Object.fromEntries(Object.entries(rangeHelper.by_month).map(([k, v]) => [k, patchSection(v)]))
      : rangeHelper.by_month,
    by_window: rangeHelper.by_window
      ? Object.fromEntries(Object.entries(rangeHelper.by_window).map(([k, v]) => [k, patchSection(v)]))
      : rangeHelper.by_window,
  };
};

const MetaPerformanceAssessmentViewer = ({ userId, authToken, shopId }) => {
  const navigate = useNavigate();
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [sharedRange, setSharedRange] = useState({
    fromMonth: '', toMonth: '', since: null, until: null, windowKey: null,
  });

  useEffect(() => {
    if (!userId || !authToken || !shopId) return undefined;
    let active = true;

    const run = async () => {
      setLoading(true);
      try {
        // Section 1: totals — fast, clears the loading screen immediately
        const totalsRes = await fetchMetaOverallReport({
          userId, shopId, authToken,
          query: { section: 'totals' },
        });
        if (!active) return;
        setReport(totalsRes?.report || null);
        setLoading(false);

        // Sections 2–4: merge each into state as they arrive
        for (const section of ['campaign_assessment', 'audience_matrix', 'ads_assessment']) {
          if (!active) break;
          try {
            const resp = await fetchMetaOverallReport({
              userId, shopId, authToken,
              query: { section },
            });
            if (!active) break;
            const sectionData = resp?.report?.data;
            if (sectionData) {
              setReport((prev) =>
                prev ? { ...prev, data: { ...prev.data, ...sectionData } } : prev
              );
            }
          } catch {
            // silent — totals data is already showing
          }
        }
      } catch {
        if (active) setLoading(false);
      }
    };

    run();
    return () => { active = false; };
  }, [userId, authToken, shopId]);

  const root = useMemo(() => report?.data || {}, [report]);
  const reportId = report?._id || null;

  const monthKeys = useMemo(() => {
    const perfRawMonths = Array.isArray(root?.campaign_performance_raw?.months)
      ? root.campaign_performance_raw.months : [];
    const helperMonths = Array.isArray(root?.campaign_performance_totals?.range_helper?.available_months)
      ? root.campaign_performance_totals.range_helper.available_months : [];
    const rawMonthKeys = perfRawMonths.map((m) => m?.month).filter(Boolean);
    return [...new Set([...helperMonths, ...rawMonthKeys])].sort().reverse();
  }, [root]);

  useEffect(() => {
    if (!monthKeys.length) {
      setSharedRange({ fromMonth: '', toMonth: '', since: null, until: null, windowKey: null });
      return;
    }
    setSharedRange((prev) => {
      const fallbackFrom = monthKeys[monthKeys.length - 1] || '';
      const fallbackTo = monthKeys[0] || '';
      const nextFrom = monthKeys.includes(prev.fromMonth) ? prev.fromMonth : fallbackFrom;
      const nextTo = monthKeys.includes(prev.toMonth) ? prev.toMonth : fallbackTo;
      const orderedFrom = nextFrom > nextTo ? fallbackFrom : nextFrom;
      const orderedTo = nextFrom > nextTo ? fallbackTo : nextTo;
      if (prev.fromMonth === orderedFrom && prev.toMonth === orderedTo) return prev;
      return { fromMonth: orderedFrom, toMonth: orderedTo, since: null, until: null, windowKey: null };
    });
  }, [monthKeys]);

  const handleRangeChange = (fromMonth, toMonth, since = null, until = null, windowKey = null) => {
    setSharedRange({ fromMonth, toMonth, since: since || null, until: until || null, windowKey: windowKey || null });
  };

  const hasOverallPayload = Boolean(
    root?.campaign_performance_totals ||
    root?.campaign_level_assessment ||
    root?.audience_efficiency_matrix_data ||
    root?.ads_level_assessment ||
    root?.summary ||
    root?.assessment ||
    root?.strategy,
  );

  if (loading) {
    return (
      <div className={styles.container}>
        <Section title="Performance">
          <Row label="Status" value="Loading performance data..." />
        </Section>
      </div>
    );
  }

  if (!hasOverallPayload) {
    return (
      <div className={styles.container}>
        <Section title="Performance">
          <Row label="Status" value="Performance data is not available yet." />
        </Section>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <OverallTab
        root={root}
        userId={userId}
        authToken={authToken}
        reportId={reportId}
        shopId={shopId}
        monthKeys={monthKeys}
        fromMonth={sharedRange.fromMonth}
        toMonth={sharedRange.toMonth}
        since={sharedRange.since}
        until={sharedRange.until}
        windowKey={sharedRange.windowKey}
        onRangeChange={handleRangeChange}
        onShowCampaignPerformance={() => navigate('/reports/open/meta')}
        onAdsetAssessmentSaved={(adsetKey, assessment) => {
          if (!adsetKey || !assessment) return;
          setReport((prev) => {
            if (!prev) return prev;
            const prevData = prev.data || {};
            const prevMatrix = prevData.audience_efficiency_matrix_data || {};
            const patch = (s) => attachAssessmentToMatrixRows(s, adsetKey, assessment);
            return {
              ...prev,
              data: {
                ...prevData,
                audience_efficiency_matrix_data: {
                  ...prevMatrix,
                  all_time: patch(prevMatrix.all_time),
                  last_month: patch(prevMatrix.last_month),
                  last_quarter: patch(prevMatrix.last_quarter),
                  last_year: patch(prevMatrix.last_year),
                  range_helper: patchRangeHelper(prevMatrix.range_helper, patch),
                  adset_assessments: { ...(prevMatrix.adset_assessments || {}), [adsetKey]: assessment },
                },
              },
            };
          });
        }}
        onAdAssessmentSaved={(adKey, assessment) => {
          if (!adKey || !assessment) return;
          setReport((prev) => {
            if (!prev) return prev;
            const prevData = prev.data || {};
            const prevAdsLevel = prevData.ads_level_assessment || {};
            const patch = (s) => attachAdAssessmentToRows(s, adKey, assessment);
            return {
              ...prev,
              data: {
                ...prevData,
                ads_level_assessment: {
                  ...prevAdsLevel,
                  all_time: patch(prevAdsLevel.all_time),
                  last_month: patch(prevAdsLevel.last_month),
                  last_quarter: patch(prevAdsLevel.last_quarter),
                  last_year: patch(prevAdsLevel.last_year),
                  range_helper: patchRangeHelper(prevAdsLevel.range_helper, patch),
                  assessments: { ...(prevAdsLevel.assessments || {}), [adKey]: assessment },
                },
              },
            };
          });
        }}
        onCampaignAssessmentSaved={(campaignKey, assessment) => {
          if (!campaignKey || !assessment) return;
          setReport((prev) => {
            if (!prev) return prev;
            const prevData = prev.data || {};
            const prevCampaignLevel = prevData.campaign_level_assessment || {};
            const patch = (s) => attachCampaignAssessmentToRows(s, campaignKey, assessment);
            return {
              ...prev,
              data: {
                ...prevData,
                campaign_level_assessment: {
                  ...prevCampaignLevel,
                  all_time: patch(prevCampaignLevel.all_time),
                  last_month: patch(prevCampaignLevel.last_month),
                  last_quarter: patch(prevCampaignLevel.last_quarter),
                  last_year: patch(prevCampaignLevel.last_year),
                  range_helper: patchRangeHelper(prevCampaignLevel.range_helper, patch),
                  assessments: { ...(prevCampaignLevel.assessments || {}), [campaignKey]: assessment },
                },
              },
            };
          });
        }}
      />
    </div>
  );
};

export default MetaPerformanceAssessmentViewer;
